import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import dayjs from "dayjs";

import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import Paggination from "./Paggination";
import { formatDateInAMPM } from "../utils/helper";
import NotificationModal from "./model/NotificationModal";

const NotificationLayer = () => {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [localPage, setLocalPage] = useState(1);
  const [localLimit, setLocalLimit] = useState(10);

  const [showModal, setShowModal] = useState(false); // ✅ Modal control state

  const { AsyncGetApiCall } = useApi();

  /* ─────────────────────────────────────────────────────────────── *
   *  FETCH ADMIN NOTIFICATIONS
   * ─────────────────────────────────────────────────────────────── */
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const apiQuery = {
        pageNo: localPage,
        limitVal: localLimit,
        search,
      };
      const res = await AsyncGetApiCall("/notifications/admin-list", apiQuery);
      if (res?.errors) {
        toast.error(res.errors[0]);
      } else {
        setResponse(res);
      }
    } catch (err) {
      toast.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPage, localLimit, search]);

  /* ─────────────────────────────────────────────────────────────── *
   *  SEARCH (debounced)
   * ─────────────────────────────────────────────────────────────── */
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setLocalPage(1);
    }, 500),
    []
  );

  const handleSearchChange = (e) => debouncedSearch(e.target.value);

  /* ─────────────────────────────────────────────────────────────── *
   *  DERIVED VALUES
   * ─────────────────────────────────────────────────────────────── */
  const notifications = response?.data ?? [];
  const pagination = response?.pagination ?? {
    total: 0,
    totalPages: 1,
    currentPage: 1,
  };

  const handleModalClose = (shouldRefetch = false) => {
    setShowModal(false);
    if (shouldRefetch) fetchNotifications();
  };

  /* ─────────────────────────────────────────────────────────────── *
   *  RENDER
   * ─────────────────────────────────────────────────────────────── */
  return (
    <>
      {loading && <Loader />}

      <div className="card h-100 p-0 radius-12">
        {/* ───── Header ───── */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center gap-3 flex-wrap w-100">
            <span className="text-md fw-medium text-secondary-light">Show</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={localLimit}
              onChange={(e) => {
                setLocalLimit(Number(e.target.value));
                setLocalPage(1);
              }}
            >
              {[5, 10, 20, 50, 100].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary d-flex align-items-center gap-2 ms-auto"
            >
              <Icon icon="mdi:plus" className="text-lg" />
              Create
            </button>

          </div>
        </div>

        {/* ───── Table ───── */}
        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Name</th>
                  {/* <th>Wallet Address</th> */}
                  <th>Title</th>
                  <th>Description</th>
                  <th>Created At</th>
                </tr>
              </thead>

              <tbody>
                {notifications.length ? (
                  notifications.map((n, i) => {
                    const rowNo = (localPage - 1) * localLimit + i + 1;
                    return (
                      <tr key={n?._id}>
                        <td>{rowNo}</td>
                        {/* <td>{n.Username || "Admin"}({n.walletAddress || ""})</td> */}
                        <td>{n?.Username || "Admin"}</td>
                        {/* <td>{n.walletAddress || "—"}</td> */}
                        <td>{n?.title}</td>
                        <td>
                          {n?.description?.length > 50
                            ? `${n?.description.slice(0, 50)}…`
                            : n?.description}
                        </td>
                        <td>{formatDateInAMPM(n?.createdAt)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No notifications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ───── Pagination ───── */}
          <Paggination
            total={pagination.total}
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            startFrom={(localPage - 1) * localLimit + 1}
            endTo={Math.min(localPage * localLimit, pagination.total)}
            setPageNo={setLocalPage}
          />
        </div>
      </div>

      <NotificationModal
        show={showModal}
        handleClose={handleModalClose}
        mode="create"
      />
    </>
  );
};

export default NotificationLayer;
